'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    number: '01',
    icon: 'DocumentArrowUpIcon',
    title: 'Candidature',
    desc: 'Remplissez le formulaire en ligne. 5 minutes suffisent. Dites-nous qui vous êtes et combien de logements vous gérez.',
    duration: '5 min',
  },
  {
    number: '02',
    icon: 'PhoneIcon',
    title: 'Entretien 15 min',
    desc: 'Un appel rapide pour valider l\'adéquation, répondre à vos questions et confirmer votre sélection.',
    duration: '48h après',
  },
  {
    number: '03',
    icon: 'KeyIcon',
    title: 'Activation du compte',
    desc: 'Votre accès premium est activé immédiatement. Connexion à vos plateformes (Airbnb, Booking, etc.) en quelques clics.',
    duration: 'Immédiat',
  },
  {
    number: '04',
    icon: 'AcademicCapIcon',
    title: 'Onboarding personnalisé',
    desc: 'Session 1h avec un expert GiteZen pour configurer votre espace selon votre structure et vos objectifs.',
    duration: 'J+3',
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-el').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 130);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="processus" className="py-28 px-6 border-t border-white/[0.05]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="reveal-el">
            <p className="text-moss-light font-bold uppercase tracking-[0.25em] text-xs mb-4">Processus de sélection</p>
            <h2 className="font-fraunces text-5xl md:text-6xl font-light italic leading-tight text-cream">
              De candidat à<br />
              <span className="text-moss-light">partenaire actif.</span>
            </h2>
          </div>
          <div className="glass-card rounded-2xl px-6 py-4 reveal-el">
            <p className="text-cream/40 text-xs uppercase tracking-widest mb-1">Disponibilité</p>
            <p className="text-cream font-semibold text-sm">Places limitées par trimestre</p>
            <p className="text-cream/40 text-xs mt-1">Sélection sur dossier et entretien</p>
          </div>
        </div>

        {/* Horizontal process cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step, i) => (
            <div key={i} className="reveal-el group relative">
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(100%+8px)] w-4 h-px bg-moss/30 z-10" />
              )}
              <div className="spotlight-card glass-card rounded-2xl p-6 h-full flex flex-col gap-4 hover:border-moss/40 transition-colors duration-300">
                {/* Number + duration */}
                <div className="flex items-center justify-between">
                  <span className="font-fraunces text-4xl italic text-moss/30 font-light leading-none">
                    {step.number}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-moss-light bg-moss/10 px-2 py-1 rounded-full">
                    {step.duration}
                  </span>
                </div>
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-moss/10 border border-moss/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon name={step.icon as any} size={20} className="text-moss-light" />
                </div>
                {/* Content */}
                <div>
                  <h3 className="text-cream font-semibold text-base mb-2">{step.title}</h3>
                  <p className="text-cream/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center reveal-el">
          <a
            href="/candidature"
            className="inline-flex items-center gap-3 bg-moss text-cream px-10 py-4 rounded-full font-bold hover:bg-moss-light transition-all duration-300 shadow-xl shadow-moss/25"
          >
            <Icon name="DocumentArrowUpIcon" size={18} />
            Déposer ma candidature
          </a>
          <p className="text-cream/30 text-xs mt-4">Réponse sous 48h ouvrées garantie</p>
        </div>
      </div>
    </section>
  );
}